import { FilterUtil } from '../app/_shared/utils/filter.util';
import { IntlUtil } from '../app/_shared/utils/intl.util';
import { SortUtil } from '../app/_shared/utils/sort.util';
import { TranslationName } from './translations';

class AppLocale {
  /** This locale's exact language code, possibly including region. */
  public readonly code: string;
  /** Languages this locale also matches as a fallback */
  public readonly alias: string[];
  /** This language's name, as written in this language. */
  public readonly endonym: string;
  /** The name of the translation file this locale uses. */
  public readonly translation: TranslationName;
  /** This locale's flag. */
  public readonly flag: string;
  /** Whether this can be used as a fallback language. */
  public readonly fallback: boolean;

  constructor(options: { code: string; translation: TranslationName; flag: string; alias?: string[]; fallback?: boolean }) {
    this.code = options.code;
    this.translation = options.translation;
    this.flag = options.flag;
    this.fallback = options.fallback ?? false;
    this.endonym = IntlUtil.getLanguageName(this.code);
    this.alias = options.alias ?? [];
  }
}

class LocaleList {
  /** The list of locales. */
  public list: AppLocale[];

  /** The universal fallback locale. */
  public fallbackLocale: AppLocale;

  /** The actual list of translations used by the list of locales. */
  public translations: TranslationName[];

  constructor(list: AppLocale[]) {
    this.list = list.toSorted((a, b) => SortUtil.compare(a.code, b.code));

    this.translations = this.list.map((locale) => locale.translation).filter(FilterUtil.unique);

    const fallbackLocale = this.list.find((locale) => locale.fallback);
    if (!fallbackLocale || this.list.filter((locale) => locale.fallback).length !== 1) {
      throw Error('Locales list should include exactly one fallback locale');
    }
    this.fallbackLocale = fallbackLocale;
  }

  /**
   * Get the locale appropriate for a given language code.
   *
   * This may be an exact match or the fallback for that language code.
   *
   * @param lang The language code to lookup.
   * @returns The locale for that langauge code
   */
  getLocale(lang: string) {
    let match = this.list.find((locale) => locale.code === lang);
    if (match) {
      return match;
    }

    match = this.list.find((locale) => locale.alias.includes(lang));
    if (match) {
      return match;
    }

    // The two letter code should always exist. If it doesn't, something is seriously wrong with the `lang` input.
    const twoLetterCode = lang.split(/[-_]/)[0];
    if (twoLetterCode) {
      match = this.list.find((locale) => locale.code === twoLetterCode || locale.alias.includes(twoLetterCode));
      if (match) {
        return match;
      }
    }

    return null;
  }
}

/** This application's locales. */
export const Locales = new LocaleList([
  new AppLocale({ code: 'en', alias: [], translation: 'en', flag: 'gb', fallback: true }),
  // new AppLocale({ code: 'en-GB', alias: [], translation: 'en', flag: 'gb' }),
  // new AppLocale({ code: 'en-US', alias: [], translation: 'en', flag: 'us' }),
  new AppLocale({ code: 'cs', alias: [], translation: 'cs', flag: 'cz' }),
  new AppLocale({ code: 'pl', alias: [], translation: 'pl', flag: 'pl' }),
]);
